import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { Separator } from "@loot-os/ui/components/separator";
import { Sheet, SheetContent, SheetTitle } from "@loot-os/ui/components/sheet";
import { cn } from "@loot-os/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { ArrowLeftIcon, ChevronRightIcon, SearchIcon } from "lucide-react";
import { useState } from "react";

import type { NavLink } from "../lib/nav.js";
import { useTenant } from "../lib/tenant.js";

const ROW =
  "flex w-full items-center justify-between rounded-lg bg-card px-4 py-3 text-base font-semibold text-card-foreground transition-colors hover:bg-muted";

/**
 * The design's mobile menu as a flyout, from the header's menu button and the tab bar's: Games
 * opens its own list of the games, then the other pages, then signing in or out.
 */
export function SiteMenu({
  open,
  onOpenChange,
  links,
  signedIn,
  onSignOut,
  onSearch,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  links: NavLink[];
  signedIn: boolean;
  onSignOut: () => void;
  onSearch: () => void;
}) {
  const { config } = useTenant();
  const [panel, setPanel] = useState<"root" | "games">("root");
  const games = links.filter((link) => link.group === "games");
  const pages = links.filter((link) => link.group === "pages");
  const close = () => onOpenChange(false);
  const inGames = panel === "games";

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setPanel("root");
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="gap-0 bg-background p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-sm"
      >
        <SheetTitle className="sr-only">{inGames ? "Games" : "Menu"}</SheetTitle>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <Link
            to="/"
            onClick={close}
            className="flex min-w-0 items-center gap-2 text-base font-semibold tracking-tight"
          >
            {config.branding.logoUrl ? (
              <img src={config.branding.logoUrl} alt="" className="h-6 w-auto" />
            ) : null}
            <span className="truncate">{config.name}</span>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Search"
              onClick={() => {
                close();
                onSearch();
              }}
            >
              <SearchIcon className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={inGames ? "Back to the menu" : "Close menu"}
              onClick={() => (inGames ? setPanel("root") : close())}
            >
              <ArrowLeftIcon className="size-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-8 overflow-y-auto p-5">
          <nav aria-label={inGames ? "Games" : "Main"} className="flex flex-col gap-4">
            {inGames ? (
              games.map((link) => (
                <Link key={link.to} to={link.to} onClick={close} className={ROW}>
                  {link.label}
                  <ChevronRightIcon className="size-4" />
                </Link>
              ))
            ) : (
              <>
                {games.length > 0 && (
                  <button type="button" onClick={() => setPanel("games")} className={ROW}>
                    Games
                    <ChevronRightIcon className="size-4" />
                  </button>
                )}
                {pages.map((link) => (
                  <Link key={link.to} to={link.to} onClick={close} className={ROW}>
                    {link.label}
                    <ChevronRightIcon className="size-4" />
                  </Link>
                ))}
              </>
            )}
          </nav>
          {!inGames && (
            <>
              <Separator />
              <div className="flex flex-col gap-3">
                {signedIn ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      close();
                      onSignOut();
                    }}
                  >
                    Sign out
                  </Button>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={close}
                      className={cn(buttonVariants({ variant: "outline" }), "w-full")}
                    >
                      Log in
                    </Link>
                    <Link to="/login" onClick={close} className={cn(buttonVariants(), "w-full")}>
                      Sign up
                    </Link>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
